/**
 * Form — react-hook-form + Zod wrapper following the shadcn pattern.
 *
 * Per code-patterns §20, forms infrastructure lives in `@flint/ui/forms`.
 *
 * `Form` is just RHF's `<FormProvider>`. The interesting parts are:
 *  - `FormField`   — RHF `<Controller>` + a per-field context exposing id/error
 *  - `FormItem`    — column-flex wrapper (replaces shadcn's class block with our token classes)
 *  - `FormControl` — `<Slot>` that wires aria-describedby / aria-invalid on the
 *                    actual input element so a11y comes for free
 *  - `FormMessage` — renders `formState.errors[name]?.message` in bad-tone
 *
 * Why we keep the full surface: it's the single most-cited shadcn pattern in
 * React form code; matching it means new contributors can read the routes
 * without learning a Flint-specific dialect.
 */
import * as React from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { Slot } from "@radix-ui/react-slot";
import { Label } from "../primitives/label.js";
import { cn } from "../lib/cn.js";

export const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = { name: TName };

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

type FormItemContextValue = { id: string };
const FormItemContext = React.createContext<FormItemContextValue | null>(null);

/** Hook used by FormLabel/FormControl/FormMessage to find their field. */
export function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  // `useFormState` rejects `undefined` under exactOptionalPropertyTypes; pass
  // the option only when we have a name. The throw a few lines down covers
  // the misuse case where `fieldContext` is null.
  const formState = useFormState(fieldContext ? { name: fieldContext.name } : {});

  if (!fieldContext) throw new Error("useFormField must be used within <FormField>");
  if (!itemContext) throw new Error("useFormField must be used within <FormItem>");

  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

export const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function FormItem({ className, ...props }, ref) {
    const id = React.useId();
    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);

export const FormLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(function FormLabel({ className, ...props }, ref) {
  const { error, formItemId } = useFormField();
  return (
    <Label
      ref={ref}
      htmlFor={formItemId}
      className={cn(error && "text-bad", className)}
      {...props}
    />
  );
});

export const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(function FormControl(props, ref) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
});

export const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function FormDescription({ className, ...props }, ref) {
  const { formDescriptionId } = useFormField();
  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-xs leading-[16px] text-muted", className)}
      {...props}
    />
  );
});

export const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function FormMessage({ className, children, ...props }, ref) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error.message ?? "") : children;
  if (!body) return null;
  return (
    <p
      ref={ref}
      id={formMessageId}
      role="alert"
      className={cn("text-xs leading-[16px] text-bad", className)}
      {...props}
    >
      {body}
    </p>
  );
});
